import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Globe } from 'lucide-react';

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: #f3f4f6;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 12px;
  width: 100%;
  height: 100%;
  max-width: 600px;
  max-height: 350px;
`;

const Card = styled(motion.div)`
  background: #ffffff;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h5`
  font-size: 12px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// Chart Components (Simplified for Visual)

const LineChartMock = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid #f3f4f6;
`;

const Bar = styled(motion.div)`
  width: 100%;
  background: #e0e7ff;
  border-radius: 2px 2px 0 0;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #6366f1;
  }
`;

const TopicRow = styled.div`
  margin-bottom: 8px;
`;

const TopicLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #4b5563;
  margin-bottom: 2px;
`;

const ProgressBar = styled.div<{ color: string }>`
  height: 4px;
  background: #f3f4f6;
  border-radius: 2px;
  overflow: hidden;
  
  div {
    height: 100%;
    background: ${props => props.color};
    border-radius: 2px;
  }
`;

const GeoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 10px;
`;

const GeoBadge = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #e0e7ff;
  color: #4338ca;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 8px;
`;

const AnalyticsVisual: React.FC = () => {
    return (
        <Container>
            <Grid>
                {/* Message Volume Trend */}
                <Card
                    style={{ gridColumn: 'span 2' }}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <CardTitle>Message Volume Trend</CardTitle>
                    <LineChartMock>
                        {[20, 40, 30, 70, 50, 20, 60, 80, 40, 50, 30, 60, 90, 40, 20].map((h, i) => (
                            <Bar
                                key={i}
                                initial={{ height: 0 }}
                                whileInView={{ height: `${h}%` }}
                                transition={{ delay: i * 0.05 }}
                            />
                        ))}
                    </LineChartMock>
                </Card>

                {/* Most Common Topics */}
                <Card
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                >
                    <CardTitle>Most Common Topics</CardTitle>
                    <TopicRow>
                        <TopicLabel><span>Course Access</span><span>25</span></TopicLabel>
                        <ProgressBar color="#10b981"><motion.div initial={{ width: 0 }} whileInView={{ width: '80%' }} transition={{ delay: 0.5 }} /></ProgressBar>
                    </TopicRow>
                    <TopicRow>
                        <TopicLabel><span>Assessment</span><span>25</span></TopicLabel>
                        <ProgressBar color="#8b5cf6"><motion.div initial={{ width: 0 }} whileInView={{ width: '80%' }} transition={{ delay: 0.6 }} /></ProgressBar>
                    </TopicRow>
                    <TopicRow>
                        <TopicLabel><span>Certificates</span><span>13</span></TopicLabel>
                        <ProgressBar color="#f59e0b"><motion.div initial={{ width: 0 }} whileInView={{ width: '40%' }} transition={{ delay: 0.7 }} /></ProgressBar>
                    </TopicRow>
                </Card>

                {/* Geographic Distribution */}
                <Card
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                >
                    <CardTitle>Geographic <Globe size={12} /></CardTitle>
                    <GeoRow>
                        <GeoBadge>IE</GeoBadge>
                        <div style={{ flex: 1 }}><ProgressBar color="#6366f1"><motion.div initial={{ width: 0 }} whileInView={{ width: '90%' }} transition={{ delay: 0.8 }} /></ProgressBar></div>
                        <span>99</span>
                    </GeoRow>
                    <GeoRow>
                        <GeoBadge>IT</GeoBadge>
                        <div style={{ flex: 1 }}><ProgressBar color="#6366f1"><motion.div initial={{ width: 0 }} whileInView={{ width: '10%' }} transition={{ delay: 0.9 }} /></ProgressBar></div>
                        <span>7</span>
                    </GeoRow>
                    <GeoRow>
                        <GeoBadge>VN</GeoBadge>
                        <div style={{ flex: 1 }}><ProgressBar color="#6366f1"><motion.div initial={{ width: 0 }} whileInView={{ width: '8%' }} transition={{ delay: 1.0 }} /></ProgressBar></div>
                        <span>6</span>
                    </GeoRow>
                </Card>
            </Grid>
        </Container>
    );
};

export default AnalyticsVisual;
